import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Key, Database, Bell, Shield, User, Save, AlertCircle, Crown, Lock, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PermissionGuard from '../components/PermissionGuard';
import UserManagement from '../components/UserManagement';
import toast from 'react-hot-toast';

const Settings: React.FC = () => {
  const { user, userRoles, userPermissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.user_metadata?.name || '',
    email: user?.email || '',
    syncFrequency: '15',
    dataRetention: '1',
    failedPaymentAlerts: true,
    syncStatusUpdates: true,
    weeklyReports: false,
    sessionTimeout: '30',
    twoFactorAuth: false,
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.user_metadata?.name || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      // In a real app, you'd call an API to update the user profile
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsUpdate = async () => {
    setLoading(true);
    try {
      // In a real app, you'd call an API to update settings
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSecurityUpdate = async () => {
    setLoading(true);
    try {
      // In a real app, you'd call an API to update security settings
      toast.success('Security settings updated successfully');
    } catch (error) {
      toast.error('Failed to update security settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async () => {
    setLoading(true);
    try {
      // In a real app, you'd call an API to update notification preferences
      toast.success('Notification preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      setLoading(true);
      try {
        // In a real app, you'd call an API to clear data
        toast.success('Data cleared successfully');
      } catch (error) {
        toast.error('Failed to clear data');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleResetConfig = async () => {
    if (window.confirm('Are you sure you want to reset all configuration? This action cannot be undone.')) {
      setLoading(true);
      try {
        // In a real app, you'd call an API to reset configuration
        toast.success('Configuration reset successfully');
      } catch (error) {
        toast.error('Failed to reset configuration');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Configure your payment dashboard preferences</p>
      </div>

      {/* User Roles & Permissions Display */}
      <PermissionGuard resource="users" action="read">
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Crown className="h-6 w-6 text-purple-600" />
            <h3 className="text-lg font-medium text-gray-900">Your Access Level</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
              <div className="space-y-2">
                {userRoles.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {role.name}
                    </span>
                    <span className="text-sm text-gray-600">{role.description}</span>
                  </div>
                ))}
                {userRoles.length === 0 && (
                  <span className="text-sm text-gray-500">No roles assigned</span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Key Permissions</label>
              <div className="space-y-2">
                {userPermissions.slice(0, 5).map((permission, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Lock className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700">
                      {permission.resource}:{permission.action}
                    </span>
                  </div>
                ))}
                {userPermissions.length > 5 && (
                  <span className="text-sm text-gray-500">
                    +{userPermissions.length - 5} more permissions
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </PermissionGuard>

      {/* Settings Sections */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* API Configuration */}
        <PermissionGuard resource="settings" action="read">
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Key className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-medium text-gray-900">API Configuration</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Supabase URL
                </label>
                <input
                  type="text"
                  value={import.meta.env.VITE_SUPABASE_URL || "https://ppnhpxhexhxewwcexwqa.supabase.co"}
                  className="input"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key (masked)
                </label>
                <input
                  type="password"
                  value="••••••••••••••••••••••••••••••••"
                  className="input"
                  readOnly
                />
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <AlertCircle className="h-4 w-4" />
                <span>API keys are managed via environment variables</span>
              </div>
            </div>
          </div>
        </PermissionGuard>

        {/* Database Settings */}
        <PermissionGuard resource="settings" action="write">
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Database className="h-6 w-6 text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">Database Settings</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sync Frequency
                </label>
                <select 
                  className="input"
                  value={formData.syncFrequency}
                  onChange={(e) => handleInputChange('syncFrequency', e.target.value)}
                >
                  <option value="15">Every 15 minutes</option>
                  <option value="30">Every 30 minutes</option>
                  <option value="60">Every hour</option>
                  <option value="360">Every 6 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Retention
                </label>
                <select 
                  className="input"
                  value={formData.dataRetention}
                  onChange={(e) => handleInputChange('dataRetention', e.target.value)}
                >
                  <option value="1">1 year</option>
                  <option value="2">2 years</option>
                  <option value="5">5 years</option>
                  <option value="0">Forever</option>
                </select>
              </div>
              <button 
                className="btn-secondary w-full flex items-center justify-center space-x-2"
                onClick={handleSettingsUpdate}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>Update Settings</span>
              </button>
            </div>
          </div>
        </PermissionGuard>

        {/* Notifications */}
        <PermissionGuard resource="settings" action="write">
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Bell className="h-6 w-6 text-yellow-600" />
              <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Failed Payment Alerts</span>
                <input
                  type="checkbox"
                  checked={formData.failedPaymentAlerts}
                  onChange={(e) => handleInputChange('failedPaymentAlerts', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Sync Status Updates</span>
                <input
                  type="checkbox"
                  checked={formData.syncStatusUpdates}
                  onChange={(e) => handleInputChange('syncStatusUpdates', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Weekly Reports</span>
                <input
                  type="checkbox"
                  checked={formData.weeklyReports}
                  onChange={(e) => handleInputChange('weeklyReports', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <button 
                className="btn-secondary w-full flex items-center justify-center space-x-2"
                onClick={handlePreferencesUpdate}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>Save Preferences</span>
              </button>
            </div>
          </div>
        </PermissionGuard>

        {/* Security */}
        <PermissionGuard resource="settings" action="write">
          <div className="card">
            <div className="flex items-center space-x-3 mb-4">
              <Shield className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900">Security</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Timeout
                </label>
                <select 
                  className="input"
                  value={formData.sessionTimeout}
                  onChange={(e) => handleInputChange('sessionTimeout', e.target.value)}
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="240">4 hours</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Two-Factor Authentication</span>
                <input
                  type="checkbox"
                  checked={formData.twoFactorAuth}
                  onChange={(e) => handleInputChange('twoFactorAuth', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </div>
              <button 
                className="btn-secondary w-full flex items-center justify-center space-x-2"
                onClick={handleSecurityUpdate}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>Update Security</span>
              </button>
            </div>
          </div>
        </PermissionGuard>
      </div>

      {/* User Profile */}
      <PermissionGuard resource="users" action="read">
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <User className="h-6 w-6 text-purple-600" />
            <h3 className="text-lg font-medium text-gray-900">User Profile</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                className="input"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <input
                type="text"
                value={userRoles.length > 0 ? userRoles.map(r => r.name).join(', ') : 'No roles assigned'}
                className="input"
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Login
              </label>
              <input
                type="text"
                value={user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'Never'}
                className="input"
                readOnly
              />
            </div>
          </div>
          <PermissionGuard resource="users" action="write">
            <div className="mt-4">
              <button 
                className="btn-primary flex items-center space-x-2"
                onClick={handleProfileUpdate}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>Update Profile</span>
              </button>
            </div>
          </PermissionGuard>
        </div>
      </PermissionGuard>

      {/* User Management Section */}
      <PermissionGuard resource="users" action="manage">
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Users className="h-6 w-6 text-indigo-600" />
            <h3 className="text-lg font-medium text-gray-900">User & Role Management</h3>
          </div>
          <UserManagement />
        </div>
      </PermissionGuard>

      {/* Danger Zone */}
      <PermissionGuard resource="settings" action="write">
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center space-x-3 mb-4">
            <SettingsIcon className="h-6 w-6 text-red-600" />
            <h3 className="text-lg font-medium text-red-900">Danger Zone</h3>
          </div>
          <div className="space-y-4">
            <p className="text-sm text-red-700">
              These actions cannot be undone. Please proceed with caution.
            </p>
            <div className="flex space-x-3">
              <button 
                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50"
                onClick={handleClearData}
                disabled={loading}
              >
                Clear All Data
              </button>
              <button 
                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50"
                onClick={handleResetConfig}
                disabled={loading}
              >
                Reset Configuration
              </button>
            </div>
          </div>
        </div>
      </PermissionGuard>
    </div>
  );
};

export default Settings;